import {
  EActionCategory,
  ECardinality,
  EDatasourceType,
  IAction,
  IActionDefinitionDto,
  IActionParameter
} from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { resolveEditTarget } from "./detached-screen-session";

export interface IDesignerActionParameterEditorOption {
  label: string;
  value: string | number;
  isDefault?: boolean;
}

export interface IDesignerActionParameterEditorConfig {
  name: string;
  options: IDesignerActionParameterEditorOption[];
  isMultiSelect?: boolean;
  hideFixedValue?: boolean;
  hideWFParams?: boolean;
  hideCategoryChange?: boolean;
  hideActionResults?: boolean;
  isLoading?: boolean;
  selectClassName?: string;
  settingsGroupName?: string;
  defaultValue?: string | number;
}

function getVisionActionOptions(sd: IScreenDesignContextState): IDesignerActionParameterEditorOption[] {
  return (
    sd.actionDefinitions
      ?.filter((actionDefinition) => actionDefinition.actionType === EDatasourceType.VisionAction)
      ?.map((actionDefinition) => ({
        label: actionDefinition.name,
        value: actionDefinition.id
      })) ?? []
  );
}

interface ICommonSettingLookup {
  id: string;
  name: string;
}

/** A client Amap setting as the settings dropdown needs it: its id resolves at runtime when it carries a REST key. */
interface IAmapClientSettingLookup extends ICommonSettingLookup {
  apiKey?: string | null;
  webServiceKey?: string | null;
}

/**
 * Credentials a map action can be wired to on an Amap project: the explicit Amap Web Service
 * settings plus every client Amap setting that carries a Web Service Key (or an Api Key). The code
 * generator writes the derived credential into the generated backend under the client setting's
 * own id, so both kinds of id resolve through MapGeocodingProviderFactory.
 */
export function getAmapMapActionSettings(
  commonSettings: Record<string, ICommonSettingLookup[] | null | undefined> | undefined
): ICommonSettingLookup[] {
  const webService: ICommonSettingLookup[] = commonSettings?.amapWebServiceSettings ?? [];
  const knownIds: Set<string> = new Set(webService.map((setting: ICommonSettingLookup): string => setting.id));
  const clients: IAmapClientSettingLookup[] = (commonSettings?.amapSettings ?? []) as IAmapClientSettingLookup[];
  const derived: ICommonSettingLookup[] = clients
    .filter(
      (client: IAmapClientSettingLookup): boolean =>
        !knownIds.has(client.id) && !!(client.webServiceKey?.trim() || client.apiKey?.trim())
    )
    .map((client: IAmapClientSettingLookup): ICommonSettingLookup => ({ id: client.id, name: client.name }));
  return [...webService, ...derived];
}

function getMcpSettingsConfig(
  sd: IScreenDesignContextState,
  actionDefinition: IActionDefinitionDto
): IDesignerActionParameterEditorConfig | undefined {
  const groupName: string | undefined = actionDefinition.mcpConfigSettingsGroupName;
  if (!groupName) return undefined;

  const commonSettings = sd.commonSettings as unknown as Record<string, ICommonSettingLookup[] | null>;
  const settingsArray: ICommonSettingLookup[] | null | undefined = commonSettings?.[groupName];

  // Map geocoding actions resolve their provider from the selected settings id at runtime — an
  // Amap id routes to Amap, a Google Map MCP id routes to Google. So for those actions
  // (mcpConfigSettingsGroupName === "googleMapMcpSettings") offer both providers' settings here,
  // letting an Amap project wire the action to its Amap credentials (see getAmapMapActionSettings).
  const amapSettings: ICommonSettingLookup[] =
    groupName === "googleMapMcpSettings" ? getAmapMapActionSettings(commonSettings) : [];
  const combinedSettings: ICommonSettingLookup[] = [...(settingsArray ?? []), ...amapSettings];

  return {
    name: "settings",
    settingsGroupName: groupName,
    options: combinedSettings.map((setting) => ({
      label: setting.name,
      value: setting.id
    })),
    isLoading: sd.isConfigListLoading,
    hideWFParams: true,
    hideCategoryChange: true,
    hideActionResults: true
  };
}

// Full MIME set the DMS recognizes (mirror of the generated controller's GetFileExtension map
// plus the text formats GenerateSkeletonFile accepts). Values must stay raw MIME strings —
// the parameter stays a String so runtime Symbol binding keeps working.
/**
 * MIME types CreateBlankDocument can actually produce a blank document for — the designer mirror of
 * BlankDocumentMimeTypes in the generated DocumentManagementController. The endpoint answers 422 for
 * anything else, so legacy binary formats (.doc/.xls/.ppt) and container formats (.epub/.djvu/.xps)
 * are deliberately absent: there is no minimal valid skeleton for them, and a zero-byte document
 * neither opens in an editor nor passes PublishDraft's empty-version check.
 */
const DMS_MIME_TYPE_OPTIONS: IDesignerActionParameterEditorOption[] = [
  {
    label: "Word (.docx)",
    value: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    isDefault: true
  },
  { label: "Excel (.xlsx)", value: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  { label: "PowerPoint (.pptx)", value: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
  { label: "OpenDocument Text (.odt)", value: "application/vnd.oasis.opendocument.text" },
  { label: "OpenDocument Spreadsheet (.ods)", value: "application/vnd.oasis.opendocument.spreadsheet" },
  { label: "OpenDocument Presentation (.odp)", value: "application/vnd.oasis.opendocument.presentation" },
  { label: "PDF (.pdf)", value: "application/pdf" },
  { label: "Rich Text (.rtf)", value: "application/rtf" },
  { label: "Plain Text (.txt)", value: "text/plain" },
  { label: "Markdown (.md)", value: "text/markdown" },
  { label: "HTML (.html)", value: "text/html" },
  { label: "CSV (.csv)", value: "text/csv" },
  { label: "JSON (.json)", value: "application/json" },
  { label: "XML (.xml)", value: "application/xml" }
];

function getExclusiveConfigs(
  sd: IScreenDesignContextState,
  action: Pick<IAction, "parameters">,
  actionDefinition: IActionDefinitionDto
): IDesignerActionParameterEditorConfig[] {
  const configs: IDesignerActionParameterEditorConfig[] = [
    {
      name: "Apps",
      options: sd.projects?.map((project) => ({ label: project.name, value: project.id })) || []
    },
    {
      name: "roleNames",
      options: sd.project?.roles?.map((role) => ({ label: role.name, value: role.name })) || [],
      isMultiSelect: true
    },
    {
      name: "startingScreenId",
      options:
        sd.screenList
          ?.sort((left, right) => left.name.localeCompare(right.name))
          ?.map((screen) => ({ label: screen.name, value: screen.id })) || [],
      hideCategoryChange: true
    },
    {
      name: "CultureName",
      options: sd.project?.languages?.map((language) => ({ value: language.name, label: language.shortName })) || []
    },
    {
      name: "columns",
      options: (() => {
        const valueParameter = action.parameters.find((parameter) => parameter.name === "value");
        if (!valueParameter?.symbol?.actionDefinitionId) {
          return [];
        }

        const valueActionDefinition = sd.actionDefinitions?.find(
          (item) => item.id === valueParameter.symbol.actionDefinitionId
        );

        return (
          valueActionDefinition?.returnTypeModel?.elements?.map((element) => ({
            label: element.name,
            value: element.name
          })) ?? []
        );
      })(),
      isMultiSelect: true
    },
    {
      name: "bucketName",
      options: [
        { label: "AWS", value: "aws" },
        { label: "Google", value: "google" },
        { label: "Azure", value: "azure" },
        { label: "MinIO", value: "minio" }
      ]
    },
    {
      name: "visionFlow",
      options: getVisionActionOptions(sd),
      hideCategoryChange: true,
      selectClassName: "vision-action-select"
    },
    {
      name: "mimeType",
      options: DMS_MIME_TYPE_OPTIONS
    }
  ];

  const actionName = actionDefinition.name;
  const actionCategory = actionDefinition.actionCategory;

  if (actionName === "GetUserTasks" && actionCategory === EActionCategory.ProcessAdministration) {
    return configs.filter((config) => config.name === "Apps");
  }

  if (actionName === "SaveUser" && actionCategory === EActionCategory.Authorization) {
    return configs.filter((config) => config.name === "roleNames" || config.name === "startingScreenId");
  }

  if (actionName === "SetLanguage" && actionCategory === EActionCategory.MultiLanguage) {
    return configs.filter((config) => config.name === "CultureName");
  }

  if (actionName === "ExportExcel" && actionCategory === EActionCategory.Export) {
    return configs.filter((config) => config.name === "columns");
  }

  if (
    (actionName === "UploadFileToBucket" && actionCategory === EActionCategory.ProcessAdministration) ||
    (actionName === "DownloadFileFromBucket" && actionCategory === EActionCategory.Device) ||
    (actionName === "DeleteFileFromBucket" && actionCategory === EActionCategory.ProcessAdministration)
  ) {
    return configs.filter((config) => config.name === "bucketName");
  }

  if (
    (actionName === "StartVisionProcess" || actionName === "StopVisionProcess") &&
    actionCategory === EActionCategory.Vision
  ) {
    return configs.filter((config) => config.name === "visionFlow");
  }

  if (actionName === "CreateBlankDocument" && actionCategory === EActionCategory.DocumentManagement) {
    return configs.filter((config) => config.name === "mimeType");
  }

  const mcpSettingsConfig = getMcpSettingsConfig(sd, actionDefinition);
  return mcpSettingsConfig ? [mcpSettingsConfig] : [];
}

export function getDesignerActionParameterEditorConfig(
  sdInstance: ScreenDesignInstance,
  action: Pick<IAction, "parameters">,
  actionDefinition: IActionDefinitionDto,
  parameter: IActionParameter,
  screenId?: string | null
): IDesignerActionParameterEditorConfig | undefined {
  // A parameter's option list is built from what the SCREEN can offer (its inputs, its components,
  // its action results), so an editor hosted over a screen the designer does not have open must be
  // offered that screen's bindables. Omitted → the open screen, exactly as before.
  const sd = resolveEditTarget(sdInstance, screenId);

  if (parameter.isEnum && parameter.enumTypeValues && parameter.enumTypeValues.length > 0) {
    return {
      name: parameter.name,
      options: parameter.enumTypeValues.map((value) => ({ label: value, value })),
      isMultiSelect: parameter.cardinality === ECardinality.Many
    };
  }

  let config = getExclusiveConfigs(sd, action, actionDefinition).find((item) => item.name === parameter.name);

  if (
    !config &&
    actionDefinition.actionType === EDatasourceType.Mqtt &&
    actionDefinition.actionTemplate.includes("publish") &&
    parameter.name === "qos"
  ) {
    config = {
      name: "qos",
      options: [
        { label: "At Most Once", value: 0, isDefault: true },
        { label: "At Least Once", value: 1 },
        { label: "Exactly Once", value: 2 }
      ]
    };
  }

  if (!config) return undefined;

  const defaultValue = config.options.find((option) => option.isDefault)?.value;
  return {
    ...config,
    options: config.options || [],
    defaultValue
  };
}
